import { CheckIcon, PenIcon, TicketIcon, TrashIcon } from "@xola/icons";
import { Badge, Dot, FadeIn, Modal, Popover, Spinner, Tooltip } from "@xola/ui-kit";
import pluralize from "pluralize";
import React, { ReactNode, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { Experience, Resource, ResourceCreatedBy, ResourceType } from "types";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { ZoomBadge } from "../../../pages/Resources/EditEquipmentPage";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ErrorBoundary } from "../../ErrorBoundary";
import { Loader } from "../../Loader";
import { DeleteEquipmentModal } from "./DeleteEquipmentModal";

const ResourceExperienceList = ({ experiences }: { experiences?: Experience[] }) => {
    if (!experiences) {
        return null;
    }

    return (
        <ul
            className="max-h-100 space-y-1 overflow-y-auto overflow-x-hidden p-2"
            data-testid="equipment-management-listing-label"
        >
            {experiences.map((experience) => (
                <li key={experience.id} className="no-translate">
                    <CheckIcon /> {experience.name}
                </li>
            ))}
        </ul>
    );
};

export interface ExperienceCount {
    count: number;
    experiences: Experience[];
}

interface EquipmentItemProps {
    resource: Resource;
    isLoading: boolean;
    experienceCount?: ExperienceCount;
}

export const EquipmentItem = ({ resource, isLoading, experienceCount }: EquipmentItemProps) => {
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [unableDeleteDialog, setUnableDeleteDialog] = useState(false);
    const toggleDeleteModal = () => setDeleteModalOpened(!deleteModalOpened);
    const toggleUnableDeleteDialog = () => setUnableDeleteDialog(!unableDeleteDialog);

    const history = useHistory();
    const handleClick = () => {
        history.push(`/resources/equipment/${resource.id}/0`);
    };

    const handleDelete = (e: any) => {
        e.stopPropagation();
        if (experienceCount && experienceCount.count > 0) {
            setUnableDeleteDialog(true);
        } else {
            setDeleteModalOpened(true);
        }
    };

    return (
        <>
            <div
                className="ui-equipment-item flex cursor-pointer items-center overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter xl:px-10"
                onClick={handleClick}
            >
                <div className="w-3/5 flex-shrink-0 space-y-1">
                    <div className="flex space-x-2">
                        <NoTranslate as="p" className="truncate text-lg font-bold leading-p2 tracking-tightest">
                            {resource.name}
                        </NoTranslate>
                        {resource.createdBy?.type === ResourceCreatedBy.ZOOM && <ZoomBadge />}
                    </div>

                    <div className="flex items-center space-x-2 leading-p1 tracking-tightest text-gray-darker">
                        {resource.count > 0 && (
                            <span data-testid="equipment-management-equipment-count">
                                Count <NoTranslate>{resource.count}</NoTranslate>
                            </span>
                        )}
                        {resource.count > 0 && resource.capacity > 0 && <Dot color="secondary" />}
                        {resource.capacity && (
                            <div data-testid="equipment-management-equipment-capacity">
                                Capacity <NoTranslate>{resource.capacity}</NoTranslate>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-auto justify-end" data-testid="equipment-management-listings-count">
                    <AssignedEquipmentDetails isLoading={isLoading} experienceCount={experienceCount} />
                </div>

                <div className="flex flex-auto justify-end space-x-2">
                    <Tooltip content="Edit">
                        <SecondaryButton
                            size="small"
                            data-testid="equipment-management-edit-equipment-button"
                            onClick={handleClick}
                        >
                            <PenIcon />
                        </SecondaryButton>
                    </Tooltip>

                    <Tooltip content="Delete">
                        <SecondaryButton
                            size="small"
                            data-testid="equipment-management-delete-equipment-button"
                            onClick={handleDelete}
                        >
                            <TrashIcon />
                        </SecondaryButton>
                    </Tooltip>
                </div>
            </div>
            <Modal
                shouldCloseOnOutsideClick
                isOpen={unableDeleteDialog}
                size="medium"
                onClose={toggleUnableDeleteDialog}
            >
                <Modal.Header>Action Required</Modal.Header>
                <Modal.Body className="max-h-75 overflow-y-auto">
                    <p className="p1" data-testid="equipment-management-modal-text-label">
                        This equipment is assigned to the following products. Please unassign them before deleting this
                        equipment.
                    </p>
                    <ResourceExperienceList experiences={experienceCount?.experiences} />
                </Modal.Body>
                <Modal.Footer>
                    <SecondaryButton onClick={toggleUnableDeleteDialog}>OK</SecondaryButton>
                </Modal.Footer>
            </Modal>
            <DeleteEquipmentModal resource={resource} isOpen={deleteModalOpened} onClose={toggleDeleteModal} />
        </>
    );
};

const EquipmentExperienceBadge = ({ value }: { value?: ReactNode }) => {
    return (
        <Badge color="secondary" icon={<TicketIcon />} size="medium" className="flex w-30 items-center">
            <FadeIn>{value ?? <Spinner size="tiny" />}</FadeIn>
        </Badge>
    );
};

export const AssignedEquipmentDetails = ({
    isLoading,
    experienceCount,
}: {
    isLoading: boolean;
    experienceCount?: ExperienceCount;
}) => {
    const listingString = experienceCount?.count ? (
        <>
            <var data-var="productCount">{experienceCount?.count}</var> {pluralize("product", experienceCount.count)}
        </>
    ) : (
        "No product"
    );

    return isLoading ? (
        <EquipmentExperienceBadge />
    ) : experienceCount?.count ? (
        <Popover content={<ResourceExperienceList experiences={experienceCount?.experiences} />}>
            <EquipmentExperienceBadge value={listingString} />
        </Popover>
    ) : (
        <EquipmentExperienceBadge value={listingString} />
    );
};

interface EquipmentListProps {
    height: number;
}

export const EquipmentManagement = ({ height }: EquipmentListProps) => {
    const {
        data: resources,
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<Resource>("resources");

    const sortedResources = useMemo(() => resources.sort((a, b) => a.name.localeCompare(b.name)), [resources]);
    const params = { "resources.resource[in]": sortedResources.map((resource) => resource.id).join(",") };
    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>(
        "experiences",
        sortedResources.length > 0 ? params : null,
    );

    const listings = useMemo(() => getExperienceAssignedToResourceRecord(experiences), [experiences]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="flex h-full flex-col border-l border-gray-lighter">
            <Virtuoso
                style={{ height }}
                data={sortedResources.filter((resource) => resource.type === ResourceType.EQUIPMENT)}
                endReached={async () => {
                    if (hasNextPage) {
                        fetchNextPage();
                    }
                }}
                itemContent={(_, resource) => {
                    return (
                        <ErrorBoundary fallbackMessage={`Error rendering equipment ${resource.id}`}>
                            <EquipmentItem
                                key={resource.id}
                                resource={resource}
                                isLoading={isFetching}
                                experienceCount={listings[resource.id]}
                            />
                        </ErrorBoundary>
                    );
                }}
            />
            {isFetchingNextPage ? <Loader /> : <div className="h-16" />}
        </div>
    );
};

/**
 * Builds a lookup map of resource IDs to the number of experiences they are assigned to,
 * along with a list of those experiences.
 */
export const getExperienceAssignedToResourceRecord = (experiences: Experience[]): Record<string, ExperienceCount> => {
    const records: Record<string, ExperienceCount> = {};
    for (const experience of experiences) {
        for (const prioritizedResource of experience.resources) {
            const resourceId = prioritizedResource.resource.id;
            const experienceCount: ExperienceCount = records[resourceId] ?? { count: 0, experiences: [] };
            experienceCount.count++;
            experienceCount.experiences = [...experienceCount.experiences, experience];
            records[resourceId] = experienceCount;
        }
    }

    return records;
};
